import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

export function useRequests(userId: string | undefined) {
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!userId) return;
    fetchRequests();
  }, [userId, refreshKey]);

  async function fetchRequests() {
    try {
      setError(null);
      setLoading(true);

      const { data: requestsData, error: requestsError } = await supabase
        .from('insurance_requests')
        .select('*, bids(amount, status, insurer_id)')
        .order('created_at', { ascending: false });

      if (requestsError) throw requestsError;

      const processedData = (requestsData || []).map(request => {
        const activeBids = request.bids?.filter(bid => bid.status === 'active') || [];
        const lowestBid = activeBids.length > 0
          ? Math.min(...activeBids.map(bid => bid.amount))
          : null;
        const userBids = activeBids.filter(bid => bid.insurer_id === userId);
        const userLowestBid = userBids.length > 0
          ? Math.min(...userBids.map(bid => bid.amount))
          : null;

        return {
          ...request,
          bid_status: activeBids.length > 0 ? 'bidding_in_progress' : 'awaiting_bids',
          lowest_bid: lowestBid,
          has_user_bid: userBids.length > 0,
          is_lowest_bidder: userLowestBid !== null && userLowestBid === lowestBid,
          user_bid_amount: userLowestBid,
          bid_count: request.bids?.length || 0,
          unique_insurers: new Set(request.bids?.map(bid => bid.insurer_id) || []).size
        };
      });

      setRequests(processedData);
    } catch (error) {
      setError('Unable to load insurance requests');
    } finally {
      setLoading(false);
    }
  }

  const refresh = () => setRefreshKey(prev => prev + 1);

  return { requests, loading, error, refresh };
}